/**
 * Open a verification document in a new tab using a short-lived signed URL.
 * Backend authorizes (provider → own docs only, admin → all docs) and issues a
 * 120-second signed URL from Supabase Storage.
 *
 * @param {string} verificationId  - the provider_verifications.id of the document
 * @param {string} accessToken     - Supabase auth access_token
 * @param {string} apiUrl          - API base, e.g. process.env.REACT_APP_BACKEND_URL + '/api'
 * @returns {Promise<{ok: true} | {ok: false, error: string}>}
 */
export async function viewProviderDocument({ verificationId, accessToken, apiUrl }) {
  if (!verificationId) return { ok: false, error: "Missing document id" };
  if (!accessToken) return { ok: false, error: "Not authenticated" };

  let res;
  try {
    res = await fetch(`${apiUrl}/provider/documents/${verificationId}/view-url`, {
      headers: { Authorization: `Bearer ${accessToken}` },
    });
  } catch (networkErr) {
    return { ok: false, error: `Network error: ${networkErr?.message || "unknown"}` };
  }

  // Read body once; tolerate Emergent fetch-interceptor pre-consumption
  let text = "";
  try {
    text = await res.text();
  } catch {
    text = "";
  }
  let body = null;
  try { body = text ? JSON.parse(text) : null; } catch { body = { raw: text }; }

  if (res.ok && body?.url) {
    // Open in a new tab. noopener/noreferrer for safety; the URL is short-lived anyway.
    window.open(body.url, "_blank", "noopener,noreferrer");
    return { ok: true };
  }

  if (res.status === 403) return { ok: false, error: "You don't have permission to view this document." };
  if (res.status === 404) return { ok: false, error: "Document not found. It may have been removed." };
  if (res.status === 401) return { ok: false, error: "Your session has expired. Please log in again." };
  const detail = body?.detail || body?.message || `HTTP ${res.status}`;
  return { ok: false, error: `Could not open document: ${detail}` };
}
